import { Toaster } from "@jl/components/ui/toaster";
import { Toaster as Sonner } from "@jl/components/ui/sonner";
import { TooltipProvider } from "@jl/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import "./index.css";
import { LanguageProvider } from "@jl/contexts/LanguageContext";
import { AuthProvider } from "@jl/contexts/AuthContext";
import { RequireStaff } from "@jl/components/RequireStaff";
import { RoutePage } from "@jl/components/RoutePage";
import { SiteLayout } from "@jl/layouts/SiteLayout";
import Index from "./pages/Index";
import Browse from "./pages/Browse";
import Contribute from "./pages/Contribute";
import Search from "./pages/Search";
import TermDetail from "./pages/TermDetail";
import Login from "./pages/Login";
import StaffLogin from "./pages/StaffLogin";
import ForgotPassword from "./pages/ForgotPassword";
import Account from "./pages/Account";
import Admin from "./pages/Admin";
import ScopeOfWork from "./pages/ScopeOfWork";
import NotFound from "./pages/NotFound";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      gcTime: 10 * 60 * 1000,
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

type JainLexiconAppProps = {
  basename?: string;
};

const App = ({ basename }: JainLexiconAppProps) => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <LanguageProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter basename={basename}>
            <Routes>
              <Route path="/" element={<SiteLayout />}>
                <Route index element={<RoutePage page={Index} />} />
                <Route path="browse" element={<RoutePage page={Browse} />} />
                <Route
                  path="contribute"
                  element={<RoutePage page={Contribute} />}
                />
                <Route path="search" element={<RoutePage page={Search} />} />
                <Route path="term/:id" element={<RoutePage page={TermDetail} />} />
                <Route path="login" element={<RoutePage page={Login} />} />
                <Route
                  path="jlx-vault-entry"
                  element={<RoutePage page={StaffLogin} />}
                />
                <Route path="staff-login" element={<Navigate to="/" replace />} />
                <Route
                  path="forgot-password"
                  element={<RoutePage page={ForgotPassword} />}
                />
                <Route
                  path="staff-forgot-password"
                  element={<RoutePage page={ForgotPassword} />}
                />
                <Route path="account" element={<RoutePage page={Account} />} />
                <Route
                  path="my-contributions"
                  element={
                    <Navigate to="/contribute#contributions" replace />
                  }
                />
                <Route
                  path="scope-of-work"
                  element={<RoutePage page={ScopeOfWork} />}
                />
                <Route path="*" element={<RoutePage page={NotFound} />} />
              </Route>

              <Route path="/admin" element={<RequireStaff />}>
                <Route index element={<Navigate to="reports" replace />} />
                <Route path=":section" element={<RoutePage page={Admin} />} />
              </Route>
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </LanguageProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
